import { useRef, useState } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faBasketShopping,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import { toastSuccess, toastError } from "../../../utils/toast";

const AddShopModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    shop_name: "",
    location: "",
    shop_type: "Retail",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const submissionInProgress = useRef(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleSubmit = async () => {
    if (submissionInProgress.current) return;

    const newErrors = {};
    if (!formData.shop_name.trim())
      newErrors.shop_name = "Shop name is required";
    if (!formData.location.trim()) newErrors.location = "Location is required";
    if (!formData.shop_type) newErrors.shop_type = "Shop type is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const shopData = {
      shop_name: formData.shop_name,
      location: formData.location,
      shop_type: formData.shop_type,
    };

    try {
      submissionInProgress.current = true;
      setIsLoading(true);
      await onAdd(shopData);
      toastSuccess("Shop added successfully!");
      onClose();
      setFormData({
        shop_name: "",
        location: "",
        shop_type: "Retail",
      });
      setErrors({});
    } catch (error) {
      console.error("Failed to add shop:", error);

      // Show specific validation errors
      if (error?.response?.data?.errors) {
        const errorData = error.response.data.errors;
        Object.keys(errorData).forEach((key) => {
          // Show the first error for each field
          toastError(errorData[key][0]);
          // Set field-specific error
          setErrors((prev) => ({ ...prev, [key]: errorData[key][0] }));
        });
      } else {
        toastError(
          error?.response?.data?.message ||
            "Failed to add shop. Please try again.",
        );
      }
    } finally {
      submissionInProgress.current = false;
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({
        shop_name: "",
        location: "",
        shop_type: "Retail",
      });
      setErrors({});
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add new shop"
      maxWidth="sm:max-w-md"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="h-4 w-4 animate-spin"
                />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Save shop
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faBasketShopping}
              className="text-blue-800 h-4 w-4"
            />
          </div>
          <div>
            <p className="text-xs text-gray-600 font-bold">Create new shop</p>
            <p className="text-xs text-gray-600 font-medium">
              Fill in all required fields (*)
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faBasketShopping}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Shop name <span className="text-red-600">*</span>
          </label>
          <Input
            name="shop_name"
            value={formData.shop_name}
            onChange={handleChange}
            placeholder="Enter shop name"
            disabled={isLoading}
            className={`border ${
              errors.shop_name ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
          />
          {errors.shop_name && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.shop_name}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium">
            Location <span className="text-red-600">*</span>
          </label>
          <Input
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder="Enter shop location"
            disabled={isLoading}
            className={`border ${
              errors.location ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
          />
          {errors.location && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.location}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium">
            Shop type <span className="text-red-600">*</span>
          </label>
          <select
            name="shop_type"
            className={`flex h-10 w-full rounded-md border ${
              errors.shop_type ? "border-red-500" : "border-gray-300"
            } bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-800/30 disabled:opacity-50 disabled:cursor-not-allowed`}
            value={formData.shop_type}
            onChange={handleChange}
            disabled={isLoading}
          >
            <option value="">Select shop type</option>
            <option value="Retail">Retail</option>
            <option value="Wholesale">Wholesale</option>
          </select>
          {errors.shop_type && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.shop_type}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddShopModal;
